<template>
  <UiModal
    v-model="isOpen"
    :width="1280"
    hide-footer
    class="flex flex-col flex-wrap"
  >
    <template #title>
      <div class="flex items-center gap-2">
        <span v-if="ls.activeItem" class="text-xl leading-5 font-semibold">{{ mediaType[ls.activeItem.type] }}</span>
      </div>
    </template>
    <div v-if="data?.id" class="space-y-6">
      <div class="flex flex-col md:flex-row gap-6">
        <div class="relative w-auto h-[272px] aspect-[404/272] max-h-[192px] rounded-md overflow-hidden shrink-0">
          <img
            v-if="data.imagemDoCardHorizontal || data.imagemDoCardVertical"
            :src="data.imagemDoCardHorizontal || data.imagemDoCardVertical"
            :alt="data.titulo"
            class="absolute inset-0 w-full h-full object-cover"
          />
          <div v-else class="fallback-cover">
            <div class="space-y-2">
              <div class="line-clamp-2 font-bold text-lg leading-5 mb-2">{{ data.titulo }}</div>
              <div class="line-clamp-2 text-xs font-light">{{ data.sinopse }}</div>
            </div>
          </div>

          <div v-if="data.dataInscricao && data.porcentagemDeConclusao" class="absolute w-full bottom-0 left-0">
            <UiProgress :percent="data.porcentagemDeConclusao" :height="4" type="danger" no-text class="" />
          </div>
        </div>
        <div class="min-w-0">
          <div class="mb-2 text-xl sm:text-2xl md:text-3xl lg:text-[40px] leading-tight lg:leading-[48px] font-semibold break-words">{{ data.titulo }}</div>
          <div class="flex gap-2">
            <CardTag
              v-for="tag in tags" :key="tag.type"
              :type="tag.type"
              :label="tag.label"
              no-shrink
              size="lg"
            />
          </div>
        </div>
      </div>

      <div class="bg-white dark:bg-neutral-800/50 rounded-md p-6 flex flex-col md:flex-row gap-x-10 gap-y-6">
        <div class="grow">
          <div class="grid grid-cols-1 md:grid-cols-2 gap-3 group/stats text-[--c-primary-700] dark:text-[--c-primary-300]">
            <div v-if="(expireDate || data.diasParaExpirar) && Number(data.diasParaExpirar) > 0" class="flex gap-1 items-center">
              <UiIcon icon="calendar-exclamation" size="20" />
              <span class="text-neutral-900 dark:text-neutral-200 font-semibold leading-5">{{ $t('column.dueDate') }}:</span>
              <span v-if="!expireDate" class="text-sm text-neutral-800 dark:text-neutral-300">{{ data.diasParaExpirar }} {{ $t('units.day', 2) }}</span>
              <span v-else class="text-sm text-neutral-800 dark:text-red-400">{{ $t('button.expired') }}</span>
            </div>

            <!-- Se expirado // Limite de Dias pare expirar -->
            <div v-if="expireDate" class="flex gap-1 items-center">
              <UiIcon icon="calendar-exclamation" size="20" />
              <span class="text-neutral-900 dark:text-neutral-200 font-semibold leading-5">{{ $t('Expirado Em') }}:</span>
              <span class="text-sm text-neutral-800 dark:text-red-400">{{ expireDate }}</span>
            </div>

            <div v-if="data.cargaHoraria" class="flex gap-1 h-5 items-center">
              <UiIcon icon="clock" size="20" />
              <span class="text-neutral-900 dark:text-neutral-200 font-semibold leading-5">{{ $t('column.duration') }}:</span>
              <span class="text-sm text-neutral-800 dark:text-neutral-300">{{ duration(data.cargaHoraria) }}</span>
            </div>

            <div class="relative h-5">
              <div class="opacity-0 group-hover/stats:opacity-100 transition-opacity">
                <UiIcon icon="thumb-up" size="20" class="absolute group-hover/stats:text-sky-400 left-0" />
              </div>

              <div class="opacity-0 group-hover/stats:opacity-100 transition-opacity">
                <UiIcon icon="heart" size="20" class="absolute left-0 group-hover/stats:text-red-400 group-hover/stats:left-4 transition-all" />
              </div>

              <div class="pl-0 group-hover/stats:pl-8 transition-all flex items-start  gap-1">
                <UiIcon icon="mood-smile" size="20" class="group-hover/stats:text-amber-400 transition-all" />
                <span class="text-neutral-900 dark:text-neutral-200 font-semibold leading-5">{{ $t('labels.reaction', 2) }}:</span>
                <span class="text-sm leading-5 text-neutral-800 dark:text-neutral-300">{{ data.totalReacoes || 0 }}</span>
              </div>
            </div>

            <div v-if="data.certificadoId" class="flex gap-1 h-5 items-center">
              <UiIcon icon="certificate" size="20" />
              <span class="text-neutral-900 dark:text-neutral-200 font-semibold leading-5">{{ $t('labels.certification') }}:</span>
              <span class="text-sm text-neutral-800 dark:text-neutral-300">{{ $t('labels.yes') }}</span>
            </div>

            <div v-if="false" class="flex gap-1 h-5 items-center">
              <UiIcon icon="rotate" size="20" />
              <span class="text-neutral-900 dark:text-neutral-200 font-semibold leading-5">{{ $t('column.updatedAt') }}:</span>
              <span class="text-sm text-neutral-800 dark:text-neutral-300">-</span>
            </div>

            <div v-if="data.dataInscricao" class="flex gap-1 h-5 items-center">
              <UiIcon icon="calendar-check" size="20" />
              <span class="text-neutral-900 dark:text-neutral-200 font-semibold leading-5">{{ $t('column.subscribedAt') }}:</span>
              <span class="text-sm text-neutral-800 dark:text-neutral-300">{{ new Date(data.dataInscricao).toLocaleDateString('pt-BR', { year: 'numeric', month: 'numeric', day: 'numeric', hour: 'numeric', minute: 'numeric' }) }}</span>
            </div>

            <div v-if="data.dataConclusao" class="flex gap-1 h-5 items-center">
              <UiIcon icon="calendar-smile" size="20" />
              <span class="text-neutral-900 dark:text-neutral-200 font-semibold leading-5">{{ $t('column.completedDate') }}:</span>
              <span class="text-sm text-neutral-800 dark:text-neutral-300">{{ new Date(data.dataConclusao).toLocaleDateString('pt-BR', { year: 'numeric', month: 'numeric', day: 'numeric', hour: 'numeric', minute: 'numeric' }) }}</span>
            </div>

            <div v-if="data.dataInscricao && data.porcentagemDeConclusao" class="flex gap-1 h-5 items-center">
              <UiIcon icon="progress-check" size="20" />
              <span class="text-neutral-900 dark:text-neutral-200 font-semibold leading-5">{{ $t('labels.progress') }}:</span>
              <span class="text-sm text-neutral-800 dark:text-neutral-300">{{ data.porcentagemDeConclusao }}%</span>
            </div>
          </div>
        </div>

        <div class="shrink-0 flex flex-col gap-4 w-[380px] max-w-full">
          <div class="flex gap-6">
            <UiButton
              :type="data.favorito ? 'success' : 'primary'"
              :icon="data.favorito ? 'bookmark-filled' : 'bookmark'"
              :outline="!data.favorito"
              class="block w-full h-12"
              :loading="busy > 0"
              @click="toggleFavorite"
            />
            <UiButton
              v-if="isSupported"
              icon="share"
              outline
              class="block w-full h-12"
              @click="() => startShare()"
            />
          </div>

          <UiButton 
            v-if="checkConsumptionControl().isBlocked || checkPrerequisites().isBlocked" 
            icon="lock" 
            class="h-10 px-4" 
            :loading="busy > 0" 
          >
            {{ checkConsumptionControl().isBlocked ? $t('placeholder.availableAt', { date: checkConsumptionControl().availableAt }) : $t('labels.preRequisites') }}
          </UiButton>

          <UiButton v-if="!data.dataInscricao && (!checkConsumptionControl().isBlocked && !checkPrerequisites().isBlocked)" icon="lock-open" class="h-10 px-4" :loading="busy > 0" @click="enroll">
            {{ $t('button.subscribe') }}
          </UiButton>

          <div class="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-4">
            <UiButton 
              v-if="data.dataInscricao && (!checkConsumptionControl().isBlocked && !checkPrerequisites().isBlocked)" 
              icon="player-play" 
              class="px-4 py-2.5" 
              :to="`/learning-center/view/${learnUrl.encode(ls.activeItem?.type || '', data?.id)}`" 
              @click.stop="goToClassRoom"
            >
              {{ $t(
                data.porcentagemDeConclusao > 0 && data.porcentagemDeConclusao < 100 ? 'button.continue'
                  : data.porcentagemDeConclusao === 100 ? 'button.seeAgain'
                  : 'button.start'
              ) }}
            </UiButton>
            <UiButton 
              v-if="data.dataInscricao && !data.porcentagemDeConclusao && (!checkConsumptionControl().isBlocked && !checkPrerequisites().isBlocked)" 
              type="danger" 
              icon="trash" 
              class="px-4 py-2.5" 
              :loading="busy > 0" 
              @click="unenroll"
            >
              {{ $t('button.unenroll') }}
            </UiButton>
          </div>
        </div>
      </div>

      <UiTabs :items="detailTabs">
        <template #about>
          <div class="flex flex-col md:flex-row gap-x-8 gap-y-6 pt-6">
            <div class="grow space-y-6">
              <div v-if="data.descricao" class="p-5 bg-white border border-neutral-300 dark:bg-neutral-800/50 dark:border-neutral-600 rounded-md">
                <div class="font-semibold text-xl leading-5 mb-4">{{ $t('column.description') }}</div>
                <div class="whitespace-pre-wrap text-sm">{{ data.descricao }}</div>
              </div>
            </div>

            <!-- Sem competições/competências/pré-requisitos a coluna some e a descrição ocupa a largura toda -->
            <div v-if="hasAsideContent" class="shrink-0 w-[380px] max-w-full space-y-6">
              <CompetitionPoints :competitions="data.competitions" />

              <div v-if="data.competenciasNomes?.length" class="p-5 bg-white border border-neutral-300 dark:bg-neutral-800/50 dark:border-neutral-600 rounded-md">
                <div class="font-semibold text-xl leading-5 mb-4">{{ $t('input.competency', 2) }}</div>
                <div class="flex flex-wrap gap-2">
                  <div
                    v-for="cmpt in data.competenciasNomes"
                    :key="cmpt"
                    class="bg-[--c-primary-300] text-[--c-primary-800] text-sm leading-4 px-2 py-1.5 rounded-md font-semibold"
                  >{{ cmpt }}</div>
                </div>
              </div>

              <div v-if="hasPrerequisites">
                <div class="font-semibold text-xl leading-5 mb-4">{{ $t('labels.prerequisites') }}</div>

                <div
                  v-for="prereq in mergedDetails"
                  :key="prereq.id"
                  class="p-5 bg-white border border-neutral-300 dark:bg-neutral-800/50 dark:border-neutral-600 rounded-md cursor-pointer"
                  @click="() => view(prereq.id, prereq.type)"
                >
                  <div class="flex gap-2 items-center semi">
                    <div class="p-1 bg-[--c-primary-300] rounded-md text-neutral-900">
                      <UiIcon icon="school" size="24" class="text-[--c-primary-700]" />
                    </div>
                    <div class="line-clamp-2">
                      <span class="font-semibold">{{ prereq.titulo }}</span>
                    </div>
                  </div>

                  <div class="mt-4 flex gap-2 text-sm">
                    <UiProgress
                      :percent="prereq.percentualDeProgresso"
                      :height="8"
                      no-text
                      type="primary"
                    />
                    <div class="shrink-0 w-[42px] text-neutral-500 text-right">{{ `${prereq.percentualDeProgresso | 0}%` }}</div>
                  </div>
                </div>
              </div>

              <div v-if="false">
                <div class="font-semibold text-xl leading-5 mb-4">Trilhas com esse Curso</div>
                <div>
                  ... Em Breve ...
                </div>
              </div>
            </div>
          </div>
        </template>

        <template #syllabus>
          <div class="pt-6">
            <!-- Curso -->
            <div class="li-card space-y-4">
              <div
                v-for="(item, idx) in data.itensDoCurso || []"
                :key="item.id"
                class="bg-white border border-neutral-300 dark:bg-neutral-800/50 rounded-md p-5"
              >
                <div class="flex flex-wrap items-center gap-2 mb-2">
                  <div class="h-5 flex items-center text-sm font-semibold">{{ Number(idx) + 1 }}</div>
                  <div 
                    class="text-neutral-600 dark:text-neutral-200 leading-6 whitespace-pre-wrap break-all line-clamp-1 overflow-hidden"
                    v-tooltip="{ text: item.titulo , width: 'auto', ifTruncated: true }"
                  >
                    {{ item.titulo }}
                  </div>
                  <CardTag v-if="item.cargaHoraria" icon="clock" no-shrink>{{ duration(item.cargaHoraria) }}</CardTag>
                  <CardTag :icon="{
                    imagem: 'photo',
                    video: 'video',
                    audio: 'music',
                    link: 'link',
                    scorm: 'packages',
                    documento: 'files',
                    pdf: 'file-type-pdf',
                    carousel: 'carousel-horizontal',
                    conteudolivre: 'checkup-list'
                  }?.[`${item.tipo}`.toLowerCase()]" no-shrink>{{ mediaType[item.tipo?.toLowerCase()] }}</CardTag>
                  <CardTag 
                    v-if="checkConsumptionControl(true, item).isBlocked" 
                    type="availableat"
                  >
                    {{ $t('placeholder.availableAt', { date: checkConsumptionControl(true, item).availableAt }) }}
                  </CardTag>
                </div>
                <UiExpandableText
                  :text="item.descricao"
                  class="text-sm text-neutral-600 dark:text-neutral-300 leading-6 whitespace-pre-wrap break-all line-clamp-1 overflow-hidden"
                />
              </div>
            </div>

            <!-- Trilha -->
            <div class="li-card space-y-4">
              <div
                v-for="(step, idx) in data.etapasDaTrilha || []"
                :key="step.id"
                class="bg-white border border-neutral-300 dark:bg-neutral-800/50 dark:border-neutral-600 rounded-md p-5"
              >
                <div class="flex flex-wrap items-center gap-2 mb-2">
                  <div class="h-6 w-6 flex items-center justify-center text-sm font-semibold border border-neutral-300 rounded-md">
                    {{ Number(idx) + 1 }}
                  </div>
                  <div 
                    class="text-neutral-800 dark:text-neutral-200 font-semibold leading-6 whitespace-pre-wrap break-all line-clamp-1 overflow-hidden"
                    v-tooltip="{ text: step.nome , width: 'auto', ifTruncated: true }"
                  >
                    {{ step.nome }}
                  </div>

                  <CardTag
                    icon="clock"
                    no-shrink
                    :title="fmtDuration(step.cargaHoraria)"
                  >{{ duration(step.cargaHoraria) }}</CardTag>
                  <!-- <CardTag
                    icon="flag"
                    type="neutral"
                    no-shrink
                  >{{ calcResources(step.itens) }} recursos</CardTag> -->
                  <CardTag 
                    v-if="checkConsumptionControl(true, step).isBlocked" 
                    type="availableat"
                  >
                    {{ $t('placeholder.availableAt', { date: checkConsumptionControl(true, step).availableAt }) }}
                  </CardTag>
                </div>

                <div class="pl-8 space-y-1">
                  <div v-for="item in step.itensDaEtapaDaTrilha" :key="item.id" class="text-sm text-neutral-600 dark:text-neutral-300">
                    <div 
                      v-if="`${item.tipo}`.toLowerCase() !== 'curso'" 
                      class="font-medium text-neutral-500 dark:text-neutral-400 leading-6 whitespace-pre-wrap break-all line-clamp-1 overflow-hidden"
                      v-tooltip="{ text: item.titulo , width: 'auto', ifTruncated: true }"
                    >
                      <UiIcon :icon="{
                          avaliacao: 'writing',
                          imagem: 'photo',
                          video: 'video',
                          audio: 'music',
                          link: 'link',
                          scorm: 'packages',
                          documento: 'files',
                          pdf: 'file-type-pdf',
                          carousel: 'carousel-horizontal'
                        }?.[`${item.tipo}`.toLowerCase()]"
                        size="24"
                        class="mr-3"
                      />
                      {{ item.titulo }}

                      <CardTag 
                        v-if="checkConsumptionControl(true, item).isBlocked" 
                        type="availableat"
                        size="sm"
                        class="ml-2"
                      >
                        {{ $t('placeholder.availableAt', { date: checkConsumptionControl(true, item).availableAt }) }}
                      </CardTag>
                    </div>


                    <ItemCourse v-else :data="item" :is-blocked="checkConsumptionControl(true, item).isBlocked" :availableAt="checkConsumptionControl(true, item).availableAt || undefined">
                      <div class="pl-0 md:pl-8 space-y-2 border-b border-b-neutral-200 dark:border-b-neutral-600 pb-4 pt-2 mb-4">
                        <div v-for="cItem in item.itensDoCurso" :key="cItem.id" class="text-sm text-neutral-600 dark:text-neutral-300">
                          <div class="font-medium text-neutral-500 dark:text-neutral-400">
                            <UiIcon :icon="{
                                avaliacao: 'writing',
                                imagem: 'photo',
                                video: 'video',
                                audio: 'music',
                                link: 'link',
                                scorm: 'packages',
                                documento: 'files',
                                pdf: 'file-type-pdf',
                                carousel: 'carousel-horizontal'
                              }?.[`${cItem.tipo}`.toLowerCase()]"
                              size="24"
                              class="mr-3"
                            />
                            {{ cItem.titulo }} <span v-if="cItem.cargaHoraria">({{ duration(cItem.cargaHoraria) }})</span>
                          </div>
                        </div>
                        <!-- <div class=" mb-2"></div> -->
                      </div>
                    </ItemCourse>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </template>
      </UiTabs>

      <!-- Relacionados: abaixo das abas (no detalhe de mídia seguem na lateral) -->
      <RelatedContent
        v-if="ls.activeItem?.parentSection"
        :section="ls.activeItem.parentSection"
        :type="['course', 'trail']"
        orientation="horizontal"
      />
    </div>
    <div v-else>
      <UiAlert
        type="info"
        :title="$t('feedback.attention')"
        :text="$t('feedback.resourceUnavailable')"
      />
    </div>
  </UiModal>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue'
import { useShare } from '@vueuse/core'
import { useLearningStore } from '@/stores/learningStore'
import { i18N, mediaType, duration, fmtDuration, learnUrl, formatDateTime, competitionPoints } from '@/common/util'
import router from '@/router'

import UiAlert from '@/components/ui/alert.vue'
import UiButton from '@/components/ui/button.vue'
import UiExpandableText from '@/components/ui/expandableText.vue'
import UiIcon from '@/components/ui/icon.vue'
import UiModal from '@/components/ui/modal.vue'
import UiTabs from '@/components/ui/tabs.vue'

import CardTag from './cardTag.vue'
import CompetitionPoints from './competitionPoints.vue'
import RelatedContent from './relatedContent.vue'
import ItemCourse from './learningDetailItemCourse.vue'
import UiProgress from '@/components/ui/progress.vue'

const isOpen = defineModel<boolean>({
  default: true
})

const busy = ref(0)
const ls = useLearningStore()
const data = computed(() => ls.activeItem?.data || {})

const itemType = computed(() => data.value.tipo === 'Curso' ? 'course' : 'trail')

const detailTabs = computed(() => [
  { id: 'about', label: i18N(itemType.value === 'course' ? 'labels.aboutCourse' : 'labels.aboutTrail') },
  { id: 'syllabus', label: i18N('labels.syllabus') }
])

const hasPrerequisites = computed(() => {
  const dependencies = data.value.parametrizacaoConteudo?.dependenciasExternas

  return !!data.value.parametrizacaoConteudo?.possuiPreRequisito &&
    !!(dependencies?.cursoDetalhes?.length || dependencies?.trilhaDetalhes?.length)
})

// Coluna lateral da aba "Sobre": sem nada para mostrar ela nem é renderizada, para a descrição não ficar
// com um vão vazio à direita
const hasAsideContent = computed(() =>
  competitionPoints(data.value.competitions).length > 0 ||
  !!data.value.competenciasNomes?.length ||
  hasPrerequisites.value
)

const expireDate = computed(() => {
  if (!data.value.expirado) return false

  const expirationDate = new Date(data.value.dataInscricao)
  expirationDate.setDate(expirationDate.getDate() + data.value.limiteDeDiasParaConclusao)

  return expirationDate.toLocaleDateString('pt-BR', { year: 'numeric', month: 'numeric', day: 'numeric', hour: 'numeric', minute: 'numeric' })
})

const { share, isSupported } = useShare()

const startShare = () => {
  share({
    title: data.value.titulo,
    text: data.value.descricao,
    url: `${location.origin}/catalog?view=${learnUrl.encode(ls.activeItem?.type || '', data.value.id)}`,
  })
}

const tags = computed(() => {
  const t : { type: string, label: string }[] = []

  t.push({
    type: itemType.value,
    label: mediaType.value[itemType.value]
  })

  if (data.value.mandatory) {
    t.push({
      type: 'required',
      label: i18N('column.required')
    })
  }

  if (checkConsumptionControl().isBlocked) {
    t.push({
      type: 'availableat',
      label: i18N('placeholder.availableAt', { date: checkConsumptionControl().availableAt })
    })
  }

  const top10 = ls.isTop10(data.value.id, itemType.value)
  if (top10) {
    t.push({
      type: 'top10',
      label: `#${top10}`
    })
  }

  if (ls.isReleases(data.value.id, itemType.value)) {
    t.push({
      type: 'new',
      label: i18N('labels.release')
    })
  }

  return t
})

const mergedDetails = computed(() => {
  const parameters = data.value?.parametrizacaoConteudo?.dependenciasExternas 
  if (!parameters) return []

  const courses = (parameters.cursoDetalhes || []).map(c => ({
    ...c,
    type: 'course'
  }))

  const trails = (parameters.trilhaDetalhes || []).map(t => ({
    ...t,
    type: 'trail'
  }))

  return [...courses, ...trails]
})

// const calcResources = (items: any[]) => {
//   let total = 0

//   items.forEach((item) => {
//     const type = item.tipo?.toLowerCase() || ''

//     if (type === 'curso' && item?.itens?.length) {
//       total += item.itens.length
//     } else { total++}
//   })

//   return total > 0 ? total : '-'
// }

const goToClassRoom = () => {
  if (!data.value.id) return
  router.push(`/learning-center/view/${learnUrl.encode(ls.activeItem?.type || '', data.value.id)}`)
  isOpen.value = false
}

const toggleFavorite = () => {
  if (!data.value.id || busy.value > 0) return

  busy.value++
  ls.toggleFavorite(data.value.id, data.value.tipo === 'Curso' ? 'course' : 'trail')
    .then(() => {
      if (ls.activeItem) {
        ls.activeItem.data.favorito = !ls.activeItem?.data?.favorito
      }
    })
    .finally(() => {
      busy.value--
    })
}

const enroll = () => {
  if (!data.value.id || busy.value > 0) return

  busy.value++
  ls.setEnrollment(data.value.id, data.value.tipo === 'Curso' ? 'course' : 'trail', true)
    .then(() => {
      if (ls.activeItem)
        ls.activeItem.data.dataInscricao = new Date()

      goToClassRoom()
    })
    .catch(() => { })
    .finally(() => {
      busy.value--
    })
}

const unenroll = () => {
  if (!data.value.id || busy.value > 0) return

  busy.value++
  ls.setEnrollment(data.value.id, data.value.tipo === 'Curso' ? 'course' : 'trail', false)
    .finally(() => {
      if (ls.activeItem) {
        ls.activeItem.data.dataInscricao = null
        ls.activeItem.data.porcentagemDeConclusao = null
      }

      busy.value--
    })
}

const checkConsumptionControl = (verifyItem: boolean = false, itemData?: any) => {
  const parameters = verifyItem 
    ? itemData?.controleDeConsumo 
    : data.value?.parametrizacaoConteudo?.controleDeConsumo

  if (!parameters) {
    return {
      isBlocked: false,
      availableAt: null
    }
  }

  if (parameters?.modoLiberacao === 'AQualquerMomento' || !parameters?.dataHoraLiberacao) {
    return {
      isBlocked: false,
      availableAt: null
    };
  }

  const releaseDateTime = new Date(parameters.dataHoraLiberacao)
  const currentDateTime = new Date()
  
  return {
    isBlocked: currentDateTime < releaseDateTime,
    availableAt: formatDateTime(releaseDateTime),
  }
}

const checkPrerequisites = () => {
  const parameters = data.value?.parametrizacaoConteudo?.dependenciasExternas || null
  if (!parameters) return { isBlocked: false }

  let isBlocked = false

  // Verifica cursos só se tiver IDs
  if (parameters.cursoIds?.length > 0) {
    const allCoursesDone =
      parameters.cursoDetalhes?.length > 0 &&
      parameters.cursoDetalhes.every(c => c.percentualDeProgresso === 100)

    if (!allCoursesDone) isBlocked = true
  }

  // Verifica formações só se tiver IDs
  if (parameters.formacaoIds?.length > 0) {
    const allTrailsDone =
      parameters.trilhaDetalhes?.length > 0 &&
      parameters.trilhaDetalhes.every(t => t.percentualDeProgresso === 100)

    if (!allTrailsDone) isBlocked = true
  }

  return { isBlocked }
}

const view = (id: string, type: 'course' | 'trail') => {
  if (!id || busy.value > 0) return

  busy.value++
  ls.viewItem(id, type)
    .finally(() => {
      busy.value--
    })
}
</script>

<style scoped lang="scss">
.fallback-cover {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: linear-gradient(to bottom right, var(--c-primary-600), var(--c-primary-700));
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;

  > div {
    max-width: 100%;
    width: 148px;
    transition: all 0.3s ease-in-out;
  }
}

.li-card {
  padding-left: 24px;

  > div {
    position: relative;

    &:not(:last-of-type)::before {
      content: '';
      position: absolute;
      left: -18px;
      top: 44px;
      width: 1px;
      height: calc(100% + 16px);
      background-color: #B9B8BA;
    }

    &::after {
      content: '';
      position: absolute;
      left: -23px;
      top: 32px;
      width: 12px;
      height: 12px;
      background-color: #F3F3F3;
      border: 1.5px solid #79767A;
      border-radius: 100%;
    }
  }
}
</style>